import { useState, useEffect, useCallback, useMemo } from "react";
import { Send, Loader2, X, Upload, Mail, MousePointerClick, Users, CheckCircle2, AlertTriangle, Rocket, Eye, Building2, RefreshCw, History, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const PERSONA_ORDER = ["procurement", "equipment", "operations", "project-execution", "innovation"];

function parseCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];
  const first = lines[0].toLowerCase();
  const hasHeader = first.includes("email");
  let cols = ["email", "first_name", "company"];
  let start = 0;
  if (hasHeader) {
    cols = lines[0].split(",").map((c) => c.trim().toLowerCase().replace(/\s+/g, "_"));
    start = 1;
  }
  const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const out = [];
  for (let i = start; i < lines.length; i++) {
    const parts = lines[i].split(",").map((p) => p.trim());
    let rec;
    if (hasHeader) {
      rec = {};
      cols.forEach((c, idx) => { rec[c] = parts[idx] || ""; });
      rec = { email: rec.email || "", first_name: rec.first_name || rec.firstname || rec.name || "", company: rec.company || rec.organization || "" };
    } else {
      rec = { email: parts[0] || "", first_name: parts[1] || "", company: parts[2] || "" };
    }
    if (emailRe.test(rec.email)) out.push(rec);
  }
  return out;
}

function EmailPreview({ campaign }) {
  const sample = (t) => t.replace(/\{\{first_name\}\}/g, "Alex").replace(/\{\{company\}\}/g, "Acme Group");
  return (
    <div className="bg-white text-charcoal border border-border max-h-[420px] overflow-auto" data-testid="email-preview">
      <div className="bg-ink px-6 py-4"><span className="text-white font-display font-extrabold tracking-wide">ONEHARVEST</span><span className="text-flame text-[10px] font-bold tracking-widest"> LLC</span></div>
      <div className="p-6">
        <div className="text-xs text-muted-foreground mb-3"><strong>Subject:</strong> {sample(campaign.subject)}</div>
        {campaign.paragraphs.map((p, i) => (
          <p key={i} className="mb-3 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: sample(p) }} />
        ))}
        <ul className="my-4 space-y-1.5">
          {campaign.bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-sm"><span className="text-flame font-bold">✓</span><span dangerouslySetInnerHTML={{ __html: b }} /></li>
          ))}
        </ul>
        <span className="inline-block bg-flame text-white px-6 py-3 text-sm font-bold">{campaign.cta_label} →</span>
        <p className="mt-5 text-sm">Happy to tailor this to your operation,</p>
        <p className="text-sm font-bold">The OneHarvest Team</p>
      </div>
    </div>
  );
}

function LaunchModal({ campaign, onClose, onLaunched }) {
  const [mode, setMode] = useState("single");
  const [single, setSingle] = useState({ email: "", first_name: "", company: "" });
  const [recipients, setRecipients] = useState([]);
  const [fileName, setFileName] = useState("");
  const [launching, setLaunching] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const list = useMemo(() => {
    if (mode === "single") {
      const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      return emailRe.test(single.email.trim()) ? [{ ...single, email: single.email.trim() }] : [];
    }
    return recipients;
  }, [mode, single, recipients]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ""));
      setRecipients(parsed);
      if (parsed.length === 0) toast.error("No valid emails found in that file.");
      else toast.success(`${parsed.length} valid recipient${parsed.length === 1 ? "" : "s"} loaded.`);
    };
    reader.readAsText(f);
  };

  const launch = async () => {
    if (list.length === 0) { toast.error("Add at least one valid email."); return; }
    setLaunching(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${campaign.id}/launch`, {
        origin: window.location.origin,
        recipients: list,
      });
      if (data.sent > 0) toast.success(`Launched — ${data.sent} sent${data.failed ? `, ${data.failed} failed` : ""}.`);
      else toast.error(`0 sent, ${data.failed} failed. Check the sender domain (see setup note).`);
      onLaunched();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLaunching(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-4" data-testid="launch-modal" onClick={onClose}>
      <div className="bg-charcoal border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-white/10 sticky top-0 bg-charcoal z-10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-flame/40 text-flame">{campaign.industry}</span>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-white/50">{campaign.persona}</span>
            </div>
            <h3 className="mt-2 font-display font-bold text-xl text-white">Launch Campaign</h3>
          </div>
          <button onClick={onClose} data-testid="launch-close" className="p-2 text-white/40 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="kicker text-white/50">Email that will be sent</span>
              <button onClick={() => setShowPreview((s) => !s)} data-testid="toggle-preview" className="inline-flex items-center gap-1.5 text-xs text-flame hover:underline"><Eye className="h-3.5 w-3.5" /> {showPreview ? "Hide" : "Preview"}</button>
            </div>
            {showPreview && <EmailPreview campaign={campaign} />}
          </div>

          <div>
            <div className="flex gap-2 mb-4">
              <button data-testid="mode-single" onClick={() => setMode("single")} className={`px-4 py-2 text-sm border transition-colors ${mode === "single" ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>Single email</button>
              <button data-testid="mode-list" onClick={() => setMode("list")} className={`px-4 py-2 text-sm border transition-colors ${mode === "list" ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>Upload list (CSV)</button>
            </div>

            {mode === "single" ? (
              <div className="grid sm:grid-cols-3 gap-3">
                <input data-testid="single-email" type="email" placeholder="Work email *" value={single.email} onChange={(e) => setSingle({ ...single, email: e.target.value })} className="bg-ink border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                <input data-testid="single-firstname" placeholder="First name" value={single.first_name} onChange={(e) => setSingle({ ...single, first_name: e.target.value })} className="bg-ink border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                <input data-testid="single-company" placeholder="Company" value={single.company} onChange={(e) => setSingle({ ...single, company: e.target.value })} className="bg-ink border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
              </div>
            ) : (
              <div>
                <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 py-8 cursor-pointer hover:border-flame/50 transition-colors" data-testid="csv-dropzone">
                  <Upload className="h-6 w-6 text-flame" />
                  <span className="text-sm text-white/70">{fileName || "Click to upload a CSV"}</span>
                  <span className="text-xs text-white/40">Columns: email, first_name, company (header optional)</span>
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-input" />
                </label>
                {recipients.length > 0 && (
                  <div className="mt-2 text-xs text-white/50" data-testid="csv-count">{recipients.length} valid recipient{recipients.length === 1 ? "" : "s"} ready</div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <div className="text-sm text-white/50" data-testid="recipient-summary"><Users className="h-4 w-4 inline mr-1.5 text-flame" />{list.length} recipient{list.length === 1 ? "" : "s"}</div>
            <button onClick={launch} disabled={launching || list.length === 0} data-testid="launch-btn" className="inline-flex items-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-50">
              {launching ? <><Loader2 className="h-4 w-4 animate-spin" /> Launching…</> : <><Rocket className="h-4 w-4" /> Launch Campaign</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

function StatusBadge({ status }) {
  const ok = status === "sent";
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${ok ? "text-emerald-400 border-emerald-400/40" : "text-red-400 border-red-400/40"}`}>{status}</span>
  );
}

function DeploymentDetailModal({ batchId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/admin/deployments/${batchId}`); setData(data); }
      catch { toast.error("Failed to load deployment"); }
      finally { setLoading(false); }
    })();
  }, [batchId]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-4" data-testid="deployment-detail-modal" onClick={onClose}>
      <div className="bg-charcoal border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-white/10 sticky top-0 bg-charcoal z-10">
          <div>
            <h3 className="font-display font-bold text-xl text-white">Deployment Detail</h3>
            {data && <p className="text-xs text-white/50 mt-1">{data.campaign.persona} · {data.campaign.industry} · launched {fmtDate(data.launched_at)}</p>}
          </div>
          <button onClick={onClose} data-testid="deployment-detail-close" className="p-2 text-white/40 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>
        ) : data ? (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["Recipients", data.metrics.recipients], ["Sent", data.metrics.sent], ["Clicks", data.metrics.clicks], ["Click Rate", `${data.metrics.click_rate}%`]].map(([l, v]) => (
                <div key={l} className="bg-ink border border-white/10 p-4">
                  <div className="font-display font-black text-2xl text-white">{v}</div>
                  <div className="text-[10px] text-white/50 uppercase tracking-widest mt-1">{l}</div>
                </div>
              ))}
            </div>
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm" data-testid="deployment-recipients-table">
                <thead>
                  <tr className="bg-white/[0.03] text-white/50 text-[10px] uppercase tracking-widest">
                    <th className="text-left px-4 py-3">Recipient</th>
                    <th className="text-left px-4 py-3">Company</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Clicked</th>
                    <th className="text-left px-4 py-3">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recipients.map((r, i) => (
                    <tr key={i} className="border-t border-white/5 text-white/80" data-testid={`deployment-recipient-${i}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{r.first_name || "—"}</div>
                        <div className="text-xs text-white/50">{r.email}</div>
                        {r.error && <div className="text-[10px] text-red-400/80 mt-0.5 max-w-[220px] truncate" title={r.error}>{r.error}</div>}
                      </td>
                      <td className="px-4 py-3 text-white/60">{r.company || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3">
                        {r.clicked
                          ? <span className="inline-flex items-center gap-1 text-flame text-xs font-semibold"><MousePointerClick className="h-3.5 w-3.5" /> {fmtDate(r.clicked_at)}</span>
                          : <span className="text-white/30 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-white/50 text-xs">{fmtDate(r.sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DeploymentsView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBatch, setOpenBatch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/deployments"); setRows(data); }
    catch { toast.error("Failed to load deployments"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>;
  if (rows.length === 0) return (
    <div className="text-center text-white/40 py-16 border border-dashed border-white/10" data-testid="deployments-empty">
      <Rocket className="h-8 w-8 mx-auto mb-3 text-white/20" />
      No campaigns have been deployed yet. Launch one from the “Launch Campaigns” tab.
    </div>
  );

  return (
    <div data-testid="deployments-view">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-white/50">{rows.length} deployment{rows.length === 1 ? "" : "s"}</span>
        <button onClick={load} data-testid="deployments-refresh" className="inline-flex items-center gap-1.5 text-xs text-flame hover:underline"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="deployments-table">
          <thead>
            <tr className="bg-white/[0.03] text-white/50 text-[10px] uppercase tracking-widest">
              <th className="text-left px-4 py-3">Campaign</th>
              <th className="text-left px-4 py-3">Launched</th>
              <th className="text-center px-4 py-3">Recipients</th>
              <th className="text-center px-4 py-3">Sent</th>
              <th className="text-center px-4 py-3">Failed</th>
              <th className="text-center px-4 py-3">Clicks</th>
              <th className="text-center px-4 py-3">Click Rate</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.batch_id} className="border-t border-white/5 text-white/80 hover:bg-white/[0.02]" data-testid={`deployment-row-${d.batch_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-flame/40 text-flame">{d.industry}</span>
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 text-white/50">{d.persona}</span>
                  </div>
                  <div className="text-white text-xs mt-1 max-w-[280px] truncate">{d.subject}</div>
                </td>
                <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">{fmtDate(d.launched_at)}</td>
                <td className="px-4 py-3 text-center">{d.recipients}</td>
                <td className="px-4 py-3 text-center text-emerald-400">{d.sent}</td>
                <td className="px-4 py-3 text-center text-red-400">{d.failed}</td>
                <td className="px-4 py-3 text-center text-flame font-semibold">{d.clicks}</td>
                <td className="px-4 py-3 text-center">{d.click_rate}%</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpenBatch(d.batch_id)} data-testid={`deployment-view-${d.batch_id}`} className="inline-flex items-center gap-1.5 text-xs border border-white/15 px-3 py-1.5 hover:border-flame hover:text-flame transition-colors">
                    <Eye className="h-3.5 w-3.5" /> Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openBatch && <DeploymentDetailModal batchId={openBatch} onClose={() => setOpenBatch(null)} />}
    </div>
  );
}

export default function OutreachCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState("All");
  const [industry, setIndustry] = useState("All");
  const [active, setActive] = useState(null);
  const [view, setView] = useState("campaigns");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/campaigns");
      setCampaigns(data);
    } catch (e) { toast.error("Failed to load campaigns"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const personas = useMemo(() => ["All", ...Array.from(new Set(campaigns.map((c) => c.persona)))], [campaigns]);
  const industries = useMemo(() => ["All", ...Array.from(new Set(campaigns.map((c) => c.industry)))], [campaigns]);

  const filtered = useMemo(() => campaigns
    .filter((c) => (persona === "All" || c.persona === persona) && (industry === "All" || c.industry === industry))
    .sort((a, b) => PERSONA_ORDER.indexOf(a.persona_key) - PERSONA_ORDER.indexOf(b.persona_key)),
    [campaigns, persona, industry]);

  const totals = useMemo(() => campaigns.reduce((acc, c) => ({
    sent: acc.sent + c.stats.sent, clicks: acc.clicks + c.stats.clicks, recipients: acc.recipients + c.stats.recipients,
  }), { sent: 0, clicks: 0, recipients: 0 }), [campaigns]);

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>;

  return (
    <div data-testid="outreach-panel">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-8">
        {[
          { label: "Campaigns", value: campaigns.length, icon: Send },
          { label: "Emails Sent", value: totals.sent, icon: Mail },
          { label: "CTA Clicks", value: totals.clicks, icon: MousePointerClick },
          { label: "Click Rate", value: totals.sent ? `${Math.round((totals.clicks / totals.sent) * 100)}%` : "—", icon: Users },
        ].map((c) => (
          <div key={c.label} data-testid={`outreach-stat-${c.label.toLowerCase().replace(/\W+/g, "-")}`} className="bg-ink border border-white/10 p-6">
            <div className="flex items-center justify-between"><c.icon className="h-5 w-5 text-flame" /><span className="font-display font-black text-3xl">{c.value}</span></div>
            <div className="mt-3 text-xs text-white/50 uppercase tracking-widest">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-white/10" data-testid="outreach-subtabs">
        {[{ k: "campaigns", label: "Launch Campaigns", icon: LayoutGrid }, { k: "deployments", label: "Deployments", icon: History }].map((t) => (
          <button key={t.k} data-testid={`outreach-view-${t.k}`} onClick={() => setView(t.k)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === t.k ? "border-flame text-flame" : "border-transparent text-white/50 hover:text-white"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {view === "deployments" && <DeploymentsView />}

      {view === "campaigns" && (<>
      <div className="flex items-start gap-3 p-4 border border-emerald-500/30 bg-emerald-500/5 mb-6" data-testid="domain-notice">
        <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-200/80 leading-relaxed">
          <strong>Live sending is active</strong> from <strong>kyle@oneharvestco.com</strong> (verified domain). Campaigns send to any recipient. If a launch ever reports a Resend "verify sender" error, confirm oneharvestco.com still shows <strong>Verified</strong> at resend.com/domains and that the app has been redeployed with the latest settings.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="outreach-filters">
        <span className="kicker text-white/40 mr-1">Persona</span>
        {personas.map((p) => (
          <button key={p} data-testid={`persona-${p.toLowerCase().replace(/\W+/g, "-")}`} onClick={() => setPersona(p)} className={`px-3 py-1.5 text-xs border transition-colors ${persona === p ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>{p}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="kicker text-white/40 mr-1">Industry</span>
        {industries.map((i) => (
          <button key={i} data-testid={`industry-${i.toLowerCase().replace(/\W+/g, "-")}`} onClick={() => setIndustry(i)} className={`px-3 py-1.5 text-xs border transition-colors ${industry === i ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>{i}</button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`campaign-card-${c.id}`} className="bg-ink border border-white/10 p-5 flex flex-col hover:border-flame/40 transition-colors">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-flame/40 text-flame">{c.industry}</span>
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 text-white/50">{c.persona}</span>
            </div>
            <div className="mt-3 font-display font-bold text-white leading-tight">{c.subject.replace(/\{\{company\}\}/g, "your company").replace(/\{\{first_name\}\}/g, "")}</div>
            <p className="mt-1 text-xs text-white/40 leading-snug flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {c.titles.slice(0, 3).join(" · ")}{c.titles.length > 3 ? " …" : ""}</p>

            <div className="mt-4 flex items-center gap-4 text-xs text-white/50">
              <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-white/40" /> {c.stats.sent} sent</span>
              <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5 text-flame" /> {c.stats.clicks} clicks</span>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2">
              <button onClick={() => setActive(c)} data-testid={`launch-open-${c.id}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-flame text-white px-4 py-2.5 text-sm font-semibold hover:bg-flame/90 transition-colors">
                <Send className="h-4 w-4" /> Launch
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-white/40 py-10 border border-dashed border-white/10">No campaigns match these filters.</div>}
      </div>
      </>)}

      {active && <LaunchModal campaign={active} onClose={() => setActive(null)} onLaunched={load} />}
    </div>
  );
}
